import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ChevronLeft, Plus, Trash2, Edit2, Pill, Search, Filter, X, Check, ChevronDown,
  AlertTriangle, FileDown, Pause, Play, Ban, Calendar, Clock, Bell, BellOff
} from 'lucide-react';
import { toast } from 'sonner';
import { exportSupplementsToPDF } from '../utils/pdfExport';
import TimelineButton from '../components/TimelineButton';
import { todayLocal, formatLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SupplementTracker = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [supplements, setSupplements] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReactionModal, setShowReactionModal] = useState(false);
  const [editingSupplement, setEditingSupplement] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('supplements');
  
  const [formData, setFormData] = useState({
    name: '', type: 'supplement', dosage: '', frequency: 'daily',
    time_of_day: '', brand: '', prescriber: '', start_date: todayLocal(),
    end_date: '', reason: '', status: 'active', notes: '',
    reminder_enabled: false, reminder_times: []
  });
  const [reminderTimeInput, setReminderTimeInput] = useState('08:00');

  const [reactionForm, setReactionForm] = useState({
    supplement_id: '', reaction_type: 'adverse_reaction', severity: 'mild',
    symptoms: [], date_occurred: todayLocal(), notes: ''
  });
  const [symptomInput, setSymptomInput] = useState('');

  const frequencies = ['once daily', 'twice daily', 'three times daily', 'every other day', 'weekly', 'as needed'];
  const timesOfDay = ['morning', 'afternoon', 'evening', 'bedtime', 'with meals', 'empty stomach'];
  const commonSymptoms = ['Nausea', 'Headache', 'Dizziness', 'Rash', 'Stomach upset', 'Fatigue', 'Insomnia', 'Anxiety', 'Heart racing', 'Diarrhea'];

  useEffect(() => {
    fetchSupplements();
    fetchReactions();
  }, [token]);

  const fetchSupplements = async () => {
    try {
      const response = await fetch(`${API_URL}/api/supplements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSupplements(data.supplements || []);
    } catch (error) {
      console.error('Error fetching supplements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/supplements/reactions/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setReactions(data.reactions || []);
    } catch (error) {
      console.error('Error fetching reactions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingSupplement 
        ? `${API_URL}/api/supplements/${editingSupplement.id}`
        : `${API_URL}/api/supplements`;
      
      const response = await fetch(url, {
        method: editingSupplement ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingSupplement ? 'Updated successfully' : 'Added successfully');
        setShowAddModal(false);
        setEditingSupplement(null);
        resetForm();
        fetchSupplements();
      }
    } catch (error) {
      toast.error('Error saving');
    }
  };

  const handleReactionSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${API_URL}/api/supplements/reactions?supplement_id=${reactionForm.supplement_id}&reaction_type=${reactionForm.reaction_type}&severity=${reactionForm.severity}&date_occurred=${reactionForm.date_occurred}&notes=${reactionForm.notes || ''}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ symptoms: reactionForm.symptoms })
        }
      );

      if (response.ok) {
        toast.success('Reaction recorded');
        setShowReactionModal(false);
        resetReactionForm();
        fetchReactions();
      }
    } catch (error) {
      toast.error('Error recording reaction');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await fetch(`${API_URL}/api/supplements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchSupplements();
    } catch (error) {
      toast.error('Error deleting');
    }
  };

  const handleDeleteReaction = async (id) => {
    if (!window.confirm('Delete this reaction?')) return;
    try {
      await fetch(`${API_URL}/api/supplements/reactions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchReactions();
    } catch (error) {
      toast.error('Error deleting');
    }
  };

  const handleEdit = (supplement) => {
    setEditingSupplement(supplement);
    setFormData({ ...supplement, reminder_times: supplement.reminder_times || [] });
    setShowAddModal(true);
  };

  const toggleReminder = async (supplement) => {
    try {
      const updatedData = { ...supplement, reminder_enabled: !supplement.reminder_enabled };
      if (!supplement.reminder_enabled && (!supplement.reminder_times || supplement.reminder_times.length === 0)) {
        updatedData.reminder_times = ['08:00'];
      }
      const response = await fetch(`${API_URL}/api/supplements/${supplement.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updatedData)
      });
      if (response.ok) {
        toast.success(supplement.reminder_enabled ? 'Reminder disabled' : 'Reminder enabled');
        fetchSupplements();
      }
    } catch (error) {
      toast.error('Error updating reminder');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', type: 'supplement', dosage: '', frequency: 'daily',
      time_of_day: '', brand: '', prescriber: '', start_date: todayLocal(),
      end_date: '', reason: '', status: 'active', notes: '',
      reminder_enabled: false, reminder_times: []
    });
    setReminderTimeInput('08:00');
  };

  const resetReactionForm = () => {
    setReactionForm({
      supplement_id: '', reaction_type: 'adverse_reaction', severity: 'mild',
      symptoms: [], date_occurred: todayLocal(), notes: ''
    });
    setSymptomInput('');
  };

  const addSymptom = (symptom) => {
    if (symptom && !reactionForm.symptoms.includes(symptom)) {
      setReactionForm({ ...reactionForm, symptoms: [...reactionForm.symptoms, symptom] });
      setSymptomInput('');
    }
  };

  const filteredSupplements = supplements.filter(s => {
    const matchesType = selectedType === 'all' || s.type === selectedType;
    const matchesStatus = selectedStatus === 'all' || s.status === selectedStatus;
    const matchesSearch = searchTerm === '' || s.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });

  const activeSupplements = supplements.filter(s => s.type === 'supplement' && s.status === 'active');
  const activeMedications = supplements.filter(s => s.type === 'medication' && s.status === 'active');

  const getStatusColor = (status) => {
    const colors = { active: 'bg-green-100 text-green-800', paused: 'bg-yellow-100 text-yellow-800', discontinued: 'bg-red-100 text-red-800' };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'active') return <Play className="w-3 h-3" />;
    if (status === 'paused') return <Pause className="w-3 h-3" />;
    return <Ban className="w-3 h-3" />;
  };

  const getSeverityColor = (severity) => {
    const colors = { mild: 'bg-yellow-100 text-yellow-800', moderate: 'bg-orange-100 text-orange-800', severe: 'bg-red-100 text-red-800' };
    return colors[severity] || 'bg-stone-100 text-stone-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-orange-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2" data-testid="back-button">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-nunito font-bold text-[#2D2D2D]">Supplement & Medication Tracker</h1>
                <p className="text-sm text-[#57534E]">Track supplements, medications, and reactions</p>
              </div>
            </div>
            <Button onClick={() => { resetForm(); setEditingSupplement(null); setShowAddModal(true); }} className="bg-accent hover:bg-accent/90" data-testid="add-supplement-button">
              <Plus className="w-4 h-4 mr-2" /> Add New
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Quick Actions */}
        <div className="flex gap-3 flex-wrap">
          <TimelineButton />
          <Button variant="outline" size="sm" onClick={() => { resetReactionForm(); setShowReactionModal(true); }} className="flex-1 min-w-[140px]" disabled={supplements.length === 0} data-testid="add-reaction-button">
            <AlertTriangle className="w-4 h-4 mr-2" /> Report Reaction
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportSupplementsToPDF(supplements, reactions)} className="flex-1 min-w-[140px]" disabled={supplements.length === 0} data-testid="export-pdf-button">
            <FileDown className="w-4 h-4 mr-2" /> Export PDF
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-stone-200">
          <button onClick={() => setActiveTab('supplements')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'supplements' ? 'border-accent text-accent' : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'}`}>
            Supplements & Meds ({supplements.length})
          </button>
          <button onClick={() => setActiveTab('reactions')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'reactions' ? 'border-accent text-accent' : 'border-transparent text-[#57534E] hover:text-[#2D2D2D]'}`}>
            Reactions ({reactions.length})
          </button>
        </div>

        {activeTab === 'supplements' && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
                <p className="text-2xl font-bold text-accent">{activeSupplements.length}</p>
                <p className="text-xs text-[#57534E]">Active Supplements</p>
              </Card>
              <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
                <p className="text-2xl font-bold text-primary">{activeMedications.length}</p>
                <p className="text-xs text-[#57534E]">Active Medications</p>
              </Card>
              <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{supplements.filter(s => s.status === 'paused').length}</p>
                <p className="text-xs text-[#57534E]">Paused</p>
              </Card>
              <Card className="bg-white rounded-xl border border-stone-100 p-4 text-center">
                <p className="text-2xl font-bold text-red-500">{reactions.length}</p>
                <p className="text-xs text-[#57534E]">Reactions Logged</p>
              </Card>
            </div>

            {/* Filters */}
            <Card className="bg-white rounded-xl border border-stone-100 p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#57534E]" />
                  <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
                </div>
                <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="h-10 px-3 bg-white border border-stone-200 rounded-lg text-sm" data-testid="type-filter">
                  <option value="all">All Types</option>
                  <option value="supplement">Supplements</option>
                  <option value="medication">Medications</option>
                </select>
                <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="h-10 px-3 bg-white border border-stone-200 rounded-lg text-sm" data-testid="status-filter">
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="discontinued">Discontinued</option>
                </select>
              </div>
            </Card>

            {/* List */}
            {loading ? (
              <Card className="bg-white rounded-xl border border-stone-100 p-8 text-center">
                <p className="text-[#57534E]">Loading...</p>
              </Card>
            ) : filteredSupplements.length === 0 ? (
              <Card className="bg-white rounded-xl border border-stone-100 p-8 text-center">
                <Pill className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="font-semibold text-[#2D2D2D] mb-2">No Items Yet</h3>
                <p className="text-sm text-[#57534E] mb-4">Start tracking your supplements and medications.</p>
                <Button onClick={() => { resetForm(); setShowAddModal(true); }} className="bg-accent hover:bg-accent/90">
                  <Plus className="w-4 h-4 mr-2" /> Add First Item
                </Button>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredSupplements.map((item) => (
                  <Card key={item.id} className="bg-white rounded-xl border border-stone-100 p-4" data-testid={`supplement-${item.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${item.type === 'medication' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'}`}>
                            {item.type === 'medication' ? '💊 Medication' : '🌿 Supplement'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${getStatusColor(item.status)}`}>
                            {getStatusIcon(item.status)} {item.status}
                          </span>
                        </div>
                        <h4 className="font-semibold text-[#2D2D2D] text-lg">{item.name}</h4>
                        <div className="flex items-center gap-4 mt-1 text-sm text-[#57534E]">
                          <span className="font-medium">{item.dosage}</span>
                          <span>•</span>
                          <span>{item.frequency}</span>
                          {item.time_of_day && <><span>•</span><span>{item.time_of_day}</span></>}
                        </div>
                        {item.brand && <p className="text-xs text-[#A8A29E] mt-1">Brand: {item.brand}</p>}
                        {item.reason && <p className="text-sm text-[#57534E] mt-2 bg-stone-50 p-2 rounded">{item.reason}</p>}
                        <div className="flex items-center gap-2 mt-2 text-xs text-[#A8A29E]">
                          <Calendar className="w-3 h-3" />
                          Started: {formatLocalDate(item.start_date)}
                          {item.end_date && <span>• Ended: {formatLocalDate(item.end_date)}</span>}
                        </div>
                        {item.reminder_enabled && item.reminder_times?.length > 0 && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-accent">
                            <Bell className="w-3 h-3" /> Reminders: {item.reminder_times.join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => toggleReminder(item)} 
                          className={`p-2 ${item.reminder_enabled ? 'bg-accent/10 hover:bg-accent/20' : 'hover:bg-stone-100'}`}
                          title={item.reminder_enabled ? 'Disable reminder' : 'Enable reminder'}
                          data-testid={`reminder-${item.id}`}
                        >
                          {item.reminder_enabled ? <Bell className="w-4 h-4 text-accent" /> : <BellOff className="w-4 h-4 text-[#A8A29E]" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} className="p-2 hover:bg-stone-100" data-testid={`edit-${item.id}`}>
                          <Edit2 className="w-4 h-4 text-[#57534E]" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50" data-testid={`delete-${item.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'reactions' && (
          <div className="space-y-3">
            {reactions.length === 0 ? (
              <Card className="bg-white rounded-xl border border-stone-100 p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="font-semibold text-[#2D2D2D] mb-2">No Reactions Logged</h3>
                <p className="text-sm text-[#57534E]">Track any adverse reactions or intolerances here.</p>
              </Card>
            ) : (
              reactions.map((reaction) => {
                const supplement = supplements.find(s => s.id === reaction.supplement_id);
                return (
                  <Card key={reaction.id} className="bg-white rounded-xl border border-stone-100 p-4" data-testid={`reaction-${reaction.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityColor(reaction.severity)}`}>
                            {reaction.severity.charAt(0).toUpperCase() + reaction.severity.slice(1)} Severity
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-800">
                            {reaction.reaction_type.replace('_', ' ')}
                          </span>
                        </div>
                        <h4 className="font-semibold text-[#2D2D2D]">{supplement?.name || 'Unknown Item'}</h4>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {reaction.symptoms.map((symptom, i) => (
                            <span key={i} className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded-full">{symptom}</span>
                          ))}
                        </div>
                        {reaction.notes && <p className="text-sm text-[#57534E] mt-2">{reaction.notes}</p>}
                        <p className="text-xs text-[#A8A29E] mt-2">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {formatLocalDate(reaction.date_occurred)}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteReaction(reaction.id)} className="p-2 hover:bg-red-50">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">{editingSupplement ? 'Edit' : 'Add'} Supplement/Medication</h2>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddModal(false); setEditingSupplement(null); }} className="p-2"><X className="w-5 h-5" /></Button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Type *</label>
                    <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full h-10 px-3 bg-white border border-stone-200 rounded-lg text-sm" required>
                      <option value="supplement">Supplement</option>
                      <option value="medication">Medication</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Status</label>
                    <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full h-10 px-3 bg-white border border-stone-200 rounded-lg text-sm">
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="discontinued">Discontinued</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Name *</label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Vitamin D3, Amoxicillin" required data-testid="name-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Dosage *</label>
                    <Input value={formData.dosage} onChange={(e) => setFormData({ ...formData, dosage: e.target.value })} placeholder="e.g., 5000 IU, 500mg" required data-testid="dosage-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Frequency *</label>
                    <select value={formData.frequency} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} className="w-full h-10 px-3 bg-white border border-stone-200 rounded-lg text-sm" required>
                      {frequencies.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Time of Day</label>
                    <select value={formData.time_of_day} onChange={(e) => setFormData({ ...formData, time_of_day: e.target.value })} className="w-full h-10 px-3 bg-white border border-stone-200 rounded-lg text-sm">
                      <option value="">Select...</option>
                      {timesOfDay.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Brand</label>
                    <Input value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} placeholder="e.g., Now Foods" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Start Date *</label>
                    <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">End Date</label>
                    <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
                  </div>
                </div>
                {formData.type === 'medication' && (
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Prescriber</label>
                    <Input value={formData.prescriber} onChange={(e) => setFormData({ ...formData, prescriber: e.target.value })} placeholder="e.g., Dr. Smith" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Reason for Taking</label>
                  <Input value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="e.g., Immune support, Infection treatment" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm min-h-[60px] resize-none" />
                </div>
                
                {/* Reminder Settings */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Bell className="w-5 h-5 text-accent" />
                      <label className="font-medium text-[#2D2D2D]">Reminders</label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, reminder_enabled: !formData.reminder_enabled })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.reminder_enabled ? 'bg-accent' : 'bg-stone-300'}`}
                      data-testid="reminder-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.reminder_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {formData.reminder_enabled && (
                    <div className="bg-stone-50 p-3 rounded-lg space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-[#57534E] mb-1">Add reminder times:</label>
                        <div className="flex gap-2">
                          <Input 
                            type="time" 
                            value={reminderTimeInput} 
                            onChange={(e) => setReminderTimeInput(e.target.value)} 
                            className="flex-1"
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              if (reminderTimeInput && !formData.reminder_times.includes(reminderTimeInput)) {
                                setFormData({ ...formData, reminder_times: [...formData.reminder_times, reminderTimeInput] });
                              }
                            }}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                      {formData.reminder_times.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {formData.reminder_times.map((time, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-accent/10 text-accent rounded-full text-sm">
                              <Clock className="w-3 h-3" /> {time}
                              <X 
                                className="w-3 h-3 cursor-pointer hover:text-red-500" 
                                onClick={() => setFormData({ ...formData, reminder_times: formData.reminder_times.filter((_, idx) => idx !== i) })}
                              />
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-[#A8A29E]">Note: Browser notifications will appear at these times when the app is open.</p>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowAddModal(false); setEditingSupplement(null); }} className="flex-1">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-accent hover:bg-accent/90" data-testid="save-button">
                    <Check className="w-4 h-4 mr-2" /> {editingSupplement ? 'Update' : 'Save'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Reaction Modal */}
      {showReactionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">Report Reaction</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowReactionModal(false)} className="p-2"><X className="w-5 h-5" /></Button>
              </div>
              <form onSubmit={handleReactionSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Supplement/Medication *</label>
                  <select value={reactionForm.supplement_id} onChange={(e) => setReactionForm({ ...reactionForm, supplement_id: e.target.value })} className="w-full h-10 px-3 bg-white border border-stone-200 rounded-lg text-sm" required>
                    <option value="">Select...</option>
                    {supplements.map(s => <option key={s.id} value={s.id}>{s.name} ({s.type})</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Reaction Type *</label>
                    <select value={reactionForm.reaction_type} onChange={(e) => setReactionForm({ ...reactionForm, reaction_type: e.target.value })} className="w-full h-10 px-3 bg-white border border-stone-200 rounded-lg text-sm" required>
                      <option value="adverse_reaction">Adverse Reaction</option>
                      <option value="intolerance">Intolerance</option>
                      <option value="allergy">Allergy</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Severity *</label>
                    <select value={reactionForm.severity} onChange={(e) => setReactionForm({ ...reactionForm, severity: e.target.value })} className="w-full h-10 px-3 bg-white border border-stone-200 rounded-lg text-sm" required>
                      <option value="mild">Mild</option>
                      <option value="moderate">Moderate</option>
                      <option value="severe">Severe</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Date Occurred *</label>
                  <Input type="date" value={reactionForm.date_occurred} onChange={(e) => setReactionForm({ ...reactionForm, date_occurred: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Symptoms</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {commonSymptoms.map(s => (
                      <button type="button" key={s} onClick={() => addSymptom(s)} className={`text-xs px-2 py-1 rounded-full border transition-colors ${reactionForm.symptoms.includes(s) ? 'bg-red-100 border-red-300 text-red-800' : 'bg-stone-50 border-stone-200 hover:bg-stone-100'}`}>{s}</button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={symptomInput} onChange={(e) => setSymptomInput(e.target.value)} placeholder="Add custom symptom" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSymptom(symptomInput))} />
                    <Button type="button" variant="outline" onClick={() => addSymptom(symptomInput)}>Add</Button>
                  </div>
                  {reactionForm.symptoms.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {reactionForm.symptoms.map((s, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full flex items-center gap-1">
                          {s} <X className="w-3 h-3 cursor-pointer" onClick={() => setReactionForm({ ...reactionForm, symptoms: reactionForm.symptoms.filter(sym => sym !== s) })} />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Notes</label>
                  <textarea value={reactionForm.notes} onChange={(e) => setReactionForm({ ...reactionForm, notes: e.target.value })} placeholder="Describe what happened..." className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm min-h-[80px] resize-none" />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowReactionModal(false)} className="flex-1">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-red-500 hover:bg-red-600 text-white" data-testid="save-reaction-button">
                    <AlertTriangle className="w-4 h-4 mr-2" /> Record Reaction
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SupplementTracker;
